/**
 * Phase 140B.2 Sprint 3 — Per-chapter clip action row.
 *
 * Three CTAs depending on `clip_status`:
 *   • ready      → "Play Clip" (deep-links to /streamx/clips/{id}) +
 *                  "Download" (direct hit on the clip's mp4_url)
 *   • processing → busy spinner + "Generating…" copy
 *   • draft      → same busy state, since worker is about to start
 *   • failed     → "Retry" button → re-fire POST /streamx/clips/generate
 *   • none       → "Generate Clip" button → POST /streamx/clips/generate
 *                  (used for lazy reaction/viewer spike highlights)
 *
 * Defensive: every API failure surfaces a toast and leaves the row in
 * its prior state. The "busy" flag is local so multiple rows can be
 * generating in parallel.
 */
import { useState } from 'react';
import { Play, Download, Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ChapterClipActions({ chapter, onClipUpdated }) {
  const [busy, setBusy] = useState(false);
  const status = chapter.clip_status || 'none';

  async function generate() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post('/streamx/clips/generate', {
        highlight_id: chapter.id,
      });
      const clip = res?.data;
      if (clip?.id) {
        // Optimistic: bump local chapter row so the UI re-renders into
        // 'processing' state without waiting for the next list refresh.
        toast.success('Clip queued — refresh in a minute.');
        if (onClipUpdated) onClipUpdated(chapter.id, {
          clip_id: clip.id,
          clip_status: clip.status || 'processing',
          clip_url: clip.mp4_url || null,
          clip_thumbnail_url: clip.thumbnail_url || null,
        });
      } else {
        toast.error('Could not queue clip. Try again?');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Clip generation failed.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  if (status === 'ready' && chapter.clip_url) {
    return (
      <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`chapter-${chapter.id}-clip-actions-ready`}>
        <a
          href={chapter.clip_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid={`chapter-${chapter.id}-play-clip`}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold transition-all"
        >
          <Play className="h-3 w-3 fill-white" />
          <span>Play Clip</span>
        </a>
        <a
          href={chapter.clip_url}
          download
          onClick={(e) => e.stopPropagation()}
          data-testid={`chapter-${chapter.id}-download-clip`}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#0c1a2e] border-2 border-[#22314F] hover:border-emerald-500 text-emerald-300 text-[11px] font-bold transition-all"
        >
          <Download className="h-3 w-3" />
          <span>Download</span>
        </a>
      </div>
    );
  }

  if (status === 'processing' || status === 'draft') {
    return (
      <div
        className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#0c1a2e] border-2 border-amber-500/40 text-amber-200 text-[11px] font-bold"
        data-testid={`chapter-${chapter.id}-clip-processing`}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Generating clip…</span>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`chapter-${chapter.id}-clip-failed`}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); generate(); }}
          disabled={busy}
          data-testid={`chapter-${chapter.id}-retry-clip`}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white text-[11px] font-bold transition-all"
        >
          <AlertTriangle className="h-3 w-3" />
          <span>{busy ? 'Retrying…' : 'Retry Clip'}</span>
        </button>
      </div>
    );
  }

  // status === 'none' → lazy candidate (reaction/viewer spike). Show
  // the Generate Clip CTA.
  return (
    <div className="mt-2" data-testid={`chapter-${chapter.id}-clip-none`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); generate(); }}
        disabled={busy}
        data-testid={`chapter-${chapter.id}-generate-clip`}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#FF5A1F] hover:bg-[#FF7A4F] disabled:opacity-60 text-white text-[11px] font-bold transition-all"
      >
        <Sparkles className="h-3 w-3" />
        <span>{busy ? 'Queueing…' : 'Generate Clip'}</span>
      </button>
    </div>
  );
}
